import { useMsal } from '@azure/msal-react';
import { useContext, useState } from 'react';
import { AmplitudeEventType } from '../../analytics/amplitudeEvents';
import { ClientConfig } from '../../clientConfig';
import { ClientConfigContext } from '../clientConfigContext';
import { ApiRequestLogger } from '../logger/apiRequestLogger';
import { getClientCorrelationId } from '../logger/logMetadata';
import { EventProperties, sendEventByOptions } from '../services/amplitudeClient';
import { useBrand } from './useBrand';

type UseAnalyticsOptionsWithEventSource = {
    eventSource: string;
    useJWT?: boolean;
};

type UseAnalyticsOptionsWithLogger = {
    logger: ApiRequestLogger;
    useJWT?: boolean;
};

export type UseAnalyticsOptions = UseAnalyticsOptionsWithEventSource | UseAnalyticsOptionsWithLogger;

export type UseAnalyticsResult = {
    sendEvent: (amplitudeEventType: AmplitudeEventType, eventProperties?: EventProperties) => void;
};

const useAnalyticsDefaultOptions: UseAnalyticsOptions = {
    eventSource: 'Unknown',
    useJWT: true,
};

const getSiteProperties = (brand: string) => {
    const searchParams = new URLSearchParams(document.location.search);
    const isMobile = searchParams.get('deviceType') === 'true';
    const siteDomain = isMobile ? `cep_${brand}` : window.location.hostname;
    const siteUrl = isMobile ? '' : window.location.hostname + window.location.pathname;
    return {
        site_domain: siteDomain,
        site_url: siteUrl,
        viewport: window.innerWidth + 'x' + window.innerHeight,
    };
};

export const useAnalytics = (options: UseAnalyticsOptions): UseAnalyticsResult => {
    const brand = useBrand();
    const config = { ...useAnalyticsDefaultOptions, ...options };
    const clientConfiguration = useContext<ClientConfig>(ClientConfigContext);
    const { instance, accounts } = useMsal();
    const [eventProperties, setEventProperties] = useState<EventProperties>(getSiteProperties(brand.brand));

    return {
        sendEvent: (amplitudeEventType: AmplitudeEventType, eventPropertiesArgs?: EventProperties) => {
            const newEventProperties = {
                ...eventProperties,
                ...eventPropertiesArgs,
            };

            if (config.useJWT) {
                instance
                    .acquireTokenSilent({
                        account: accounts[0],
                        scopes: clientConfiguration.b2cTenantConfig.scopes,
                        correlationId: getClientCorrelationId(),
                    })
                    .then((response: { accessToken: any }) => {
                        return response.accessToken;
                    })
                    .then((jwt) => {
                        sendEventByOptions({
                            eventProperties: newEventProperties,
                            eventType: amplitudeEventType,
                            jwt,
                        });
                    });
            } else {
                sendEventByOptions({
                    eventProperties: newEventProperties,
                    eventType: amplitudeEventType,
                });
            }
        },
    };
};
